import { Button, DialogActions, DialogContent, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/system';
import { useState } from 'react';
import { dsnPageTestId } from './test-ids';

type Props = {
  close: () => void;
  confirm: (reason: string) => void;
  patientName?: string;
  dateOfService?: string;
  therapistName?: string;
  facility?: string;
  loading?: boolean;
};

const DeleteNoteConfirmation = (props: Props) => {
  const { close, confirm, patientName, dateOfService, therapistName, facility, loading } = props;
  const [reason, setReason] = useState('');

  // Auto-filled context line from the document: Patient · Date of service · Therapist · Facility.
  const contextLine = [patientName, dateOfService, therapistName, facility]
    .filter((part) => !!part && `${part}`.trim())
    .join(' | ');

  return (
    <Box p={1}>
      <DialogContent>
        <Typography
          variant="h5"
          fontWeight={700}
        >
          Delete this Daily Session Note?
        </Typography>

        {contextLine && (
          <Typography
            variant="body2"
            sx={{ mt: 0.5 }}
          >
            {contextLine}
          </Typography>
        )}

        <Typography
          variant="body2"
          sx={{ color: '#111927', mt: 2 }}
        >
          This will permanently delete the note.{' '}
          <Typography
            component="span"
            variant="body2"
            fontWeight={700}
          >
            This cannot be undone
          </Typography>{' '}
          — the note cannot be recovered or restored once deleted.
        </Typography>

        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ color: '#111927', mt: 2, mb: 1 }}
        >
          Why are you deleting this note? (required)
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          inputProps={{ 'data-testid': dsnPageTestId('delete-reason') }}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g., documented on the wrong patient, incorrect date of service…"
          sx={{
            '& .MuiInputBase-root': {
              padding: '10px !important',
            },
          }}
        />
        <Typography
          variant="caption"
          sx={{ fontStyle: 'italic', mt: 1, display: 'block' }}
        >
          Your name and reason are recorded for auditing.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button
          data-testid={dsnPageTestId('delete-cancel')}
          sx={{ color: '#111927' }}
          onClick={close}
          disabled={loading}
        >
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={loading}
          disabled={!reason.trim() || loading}
          sx={{
            background: '#F04438',
            color: '#fff',
            '&:hover': { background: '#F04438' },
          }}
          onClick={() => confirm(reason.trim())}
          data-testid={dsnPageTestId('delete-confirm')}
        >
          Delete
        </LoadingButton>
      </DialogActions>
    </Box>
  );
};

export default DeleteNoteConfirmation;
